import React from 'react';
import { Printer, X } from 'lucide-react';
import { formatDuration } from '../../utils/formatDuration';

const PrintView = ({ appointments, selectedDate, technicianName, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  // Group appointments by date
  const appointmentsByDate = appointments.reduce((acc, apt) => {
    const date = new Date(apt.scheduled_date).toISOString().split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(apt);
    return acc;
  }, {});

  // Sort dates
  const sortedDates = Object.keys(appointmentsByDate).sort();

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateTimeStr) => {
    return new Date(dateTimeStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'text-blue-500';
      case 'confirmed': return 'text-blue-800';
      case 'completed': return 'text-green-700';
      case 'cancelled': return 'text-gray-500';
      default: return 'text-blue-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Hidden when printing */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal print:hidden">
          <h2 className="text-2xl font-bold text-white">Print Schedule</h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePrint}
              className="bg-white text-pest-blue px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center space-x-2"
            >
              <Printer size={20} />
              <span>Print</span>
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors duration-200 p-2"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Print Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header - Shown when printing */}
          <div className="print-header mb-6 hidden print:block">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-pest-blue">PestControl.co.uk</h1>
              <div className="text-right">
                <div className="text-sm text-gray-600">Printed: {new Date().toLocaleDateString('en-US')}</div>
              </div>
            </div>
            <div className="border-b-2 border-pest-blue pb-2 mb-4">
              <h2 className="text-xl font-semibold">Technician Schedule</h2>
              {technicianName && <p className="text-gray-600">Technician: {technicianName}</p>}
            </div>
          </div>

          {/* Appointments by Date */}
          {sortedDates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No appointments to print
            </div>
          ) : (
            sortedDates.map(dateStr => (
              <div key={dateStr} className="mb-8 page-break-after">
                <h3 className="text-lg font-bold text-pest-blue mb-4 pb-2 border-b-2 border-gray-200">
                  {formatDate(dateStr)}
                </h3>

                <div className="space-y-4">
                  {appointmentsByDate[dateStr]
                    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                    .map(apt => (
                      <div
                        key={apt.id}
                        className="border-2 border-gray-300 rounded-lg p-4 print:break-inside-avoid"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-xl font-semibold text-gray-900">
                              {formatTime(apt.scheduled_date)}
                            </div>
                            <div className={`text-sm font-medium ${getStatusColor(apt.status)}`}>
                              {apt.status.toUpperCase()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600">
                              Duration: {formatDuration(apt.estimated_duration || 60)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {apt.appointment_type?.replace('_', ' ').toUpperCase()}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-200">
                          <div>
                            <div className="text-xs text-gray-500 font-medium mb-1">CUSTOMER</div>
                            <div className="font-semibold text-gray-900">
                              {apt.customer_name || 'No customer'}
                            </div>
                          </div>

                          {apt.job_title && (
                            <div>
                              <div className="text-xs text-gray-500 font-medium mb-1">JOB</div>
                              <div className="text-gray-700">{apt.job_title}</div>
                            </div>
                          )}
                        </div>

                        {apt.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-500 font-medium mb-1">NOTES</div>
                            <div className="text-sm text-gray-700">{apt.notes}</div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer - Shown when printing */}
        <div className="hidden print:block text-center text-xs text-gray-500 py-4 border-t">
          PestControl.co.uk - Professional Pest Control Services
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          .print:hidden {
            display: none !important;
          }

          .print\\:block {
            display: block !important;
          }

          .print\\:break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .page-break-after {
            page-break-after: always;
          }

          .page-break-after:last-child {
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintView;
